/**
 * This is an adapted port of the angular Geometry service with the notable change of the distanceTo function
 * now being name distanceBetween, and it along with sortLocationsByDistance now accepting the
 * current user's location as an argument.  Since location is managed through a context,
 * it is not directly accessible from non-React files.
 */

const METERS_PER_MILE = 0.000621371192;

var distanceCache = {}; // `${userLat},${userLng}:${locationId}` -> distance

const Geometry = {
  clearCache: () => {
    distanceCache = {};
  },

  distanceBetween: (userLocation, location) => {
    // Safety - shouldn't get called before google is loaded or Location is available.
    if (!google || !google.maps || !google.maps.geometry || !userLocation.lat || !userLocation.lng) {
      return null;
    }

    const cacheKey = `${userLocation.lat},${userLocation.lng}:${location.id}`;
    if (distanceCache[cacheKey]) {
      return distanceCache[cacheKey];
    }
    const distance = noCacheDistanceBetween(userLocation, location);
    distanceCache[cacheKey] = distance;
    return distance;
  },

  sortLocationsByDistance: (userLocation, locations) => {
    if (!userLocation || !locations) {
      return locations;
    }
    // Sorts a copy — the input may be React props/state and must not be mutated.
    return [...locations].sort(function (loc1, loc2) {
      const dist1 = Geometry.distanceBetween(userLocation, loc1);
      const dist2 = Geometry.distanceBetween(userLocation, loc2);
      // Keep the original order when distances aren't computable (maps not loaded).
      if (dist1 === null || dist2 === null) {
        return 0;
      }
      return dist1 - dist2;
    });
  },

  sortOppsByLocation: (userLocation, opps) => {
    opps.sort(function (a, b) {
      const locA = locationRetriever(a);
      const locB = locationRetriever(b);

      // Return early if opp doesn't have lat/long
      if (!locA || !locB) {
        return 0;
      }

      const distA = noCacheDistanceBetween(userLocation, locA);
      const distB = noCacheDistanceBetween(userLocation, locB);

      if (distA < distB) {
        return -1;
      } else if (distA > distB) {
        return 1;
      } else {
        return 0;
      }
    });

    return opps;
  },

  setDistanceTo: (userLocation, opps) => {
    opps?.map((opp) => {
      const loc = locationRetriever(opp);

      if (!loc) {
        opp.locations.push({ distanceTo: null });
      } else {
        const dist = noCacheDistanceBetween(userLocation, loc);
        opp.locations[0].distanceTo = dist;
      }
    });
    return opps;
  },

  nearestDistanceTo: (userLocation, locations) => {
    if (!userLocation || !locations?.length) return null;
    return locations.reduce((min, loc) => {
      const d = Geometry.distanceBetween(userLocation, loc);
      if (d === null) return min;
      return min === null || d < min ? d : min;
    }, null);
  },

  sortByDistanceTo: (opps) => {
    opps?.sort(function (a, b) {
      const distA = a.locations[0]?.distanceTo;
      const distB = b.locations[0]?.distanceTo;

      if (distA < distB) {
        return -1;
      } else if (distA > distB) {
        return 1;
      } else {
        return 0;
      }
    });

    return opps;
  }
};

const locationRetriever = (opp) => {
  if (opp.locations.length == 0) {
    return null;
  }

  const lat = opp.locations[0]?.lat;
  const long = opp.locations[0]?.long;

  if (lat && long) {
    return { lat: lat, long: long };
  } else {
    return null;
  }
};

const noCacheDistanceBetween = (userLocation, location) => {
  if (!google || !google.maps || !google.maps.geometry) {
    return 0;
  }

  const userLat = userLocation.lat;
  const userLong = userLocation.long ? userLocation.long : userLocation.lng;
  const locationLat = location.lat;
  const locationLong = location.long ? location.long : location.lng;

  const distance =
    METERS_PER_MILE *
    google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(userLat, userLong),
      new google.maps.LatLng(locationLat, locationLong)
    );
  return distance;
};

export default Geometry;
